import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Check,
  Phone,
  ChevronDown,
  ShieldCheck,
  PartyPopper,
} from "lucide-react";
import { toast } from "sonner";
import { classifyIncome, thresholdsFor, formatEuro } from "@/lib/income";
import { getUtm } from "@/lib/utm";
import { trackPixel } from "@/lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS_META = ["Logement", "Chauffage", "Revenus", "Contact"];
const MPR = { "Très modestes": 5000, "Modestes": 4000, "Intermédiaires": 3000, "Supérieurs": 0 };
const CEE = { "Très modestes": 5800, "Modestes": 4800, "Intermédiaires": 3800, "Supérieurs": 2800 };

const OPTIONS = {
  property_type: ["Maison", "Appartement"],
  construction_period: ["Plus de 15 ans", "Moins de 15 ans"],
  surface_range: ["Moins de 70 m²", "70 à 110 m²", "110 à 150 m²", "Plus de 150 m²"],
  heating_current: ["Chaudière fioul", "Chaudière gaz", "Électrique", "Autre"],
  emitters: [
    "Radiateurs haute température",
    "Radiateurs basse température",
    "Plancher chauffant",
  ],
};

const slug = (v) => v.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase().replace(/^-|-$/g, "");
const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-white placeholder-white/40 outline-none transition-colors focus:border-emerald-400";
const labelCls =
  "block font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-300";

const INITIAL = {
  property_type: "",
  construction_period: "",
  surface_range: "",
  heating_current: "",
  emitters: "",
  postal_code: "",
  household_size: "",
  rfr: "",
  income_bracket: "",
  contact_name: "",
  phone: "",
  email: "",
  consent: false,
};

const Option = ({ field, value, data, setData }) => {
  const active = data[field] === value;
  return (
    <button
      type="button"
      data-testid={`simulator-option-${field}-${slug(value)}`}
      onClick={() => setData((d) => ({ ...d, [field]: value }))}
      className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3.5 text-left text-sm font-semibold transition-all ${
        active
          ? "border-emerald-400 bg-emerald-400/10 text-white shadow-glow"
          : "border-white/15 bg-white/5 text-white/65 hover:border-white/40 hover:text-white"
      }`}
    >
      {value}
      {active && <Check className="h-4 w-4 shrink-0 text-emerald-400" />}
    </button>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-3">
    <span className={labelCls}>{label}</span>
    {children}
  </div>
);

export default function Simulator() {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const aids = useMemo(() => {
    if (!data.income_bracket) return null;
    if (data.property_type !== "Maison" || data.construction_period !== "Plus de 15 ans")
      return { type: "study" };
    return { type: "aids", total: MPR[data.income_bracket] + CEE[data.income_bracket] };
  }, [data.property_type, data.construction_period, data.income_bracket]);

  const aidsLabel =
    aids?.type === "aids"
      ? `≈ ${aids.total.toLocaleString("fr-FR")} € (MaPrimeRénov' + CEE)`
      : "Étude personnalisée";

  const canNext = () => {
    if (step === 1)
      return data.property_type && data.construction_period && data.surface_range;
    if (step === 2) return data.heating_current && data.emitters;
    if (step === 3)
      return (
        /^[0-9]{5}$/.test(data.postal_code) &&
        data.household_size &&
        data.rfr !== "" &&
        data.income_bracket
      );
    return false;
  };

  const canSubmit = () => {
    const digits = data.phone.replace(/\D/g, "");
    return (
      data.contact_name.trim().length >= 2 &&
      digits.length >= 10 &&
      digits.length <= 13 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email) &&
      data.consent
    );
  };

  const submit = async () => {
    if (!canSubmit()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...data, ...getUtm(), estimated_aids: aidsLabel });
      trackPixel("Lead", { content_name: "pompe-a-chaleur", content_category: data.heating_current });
      setDone(true);
    } catch (e) {
      toast.error("Une erreur est survenue. Réessayez ou appelez-nous.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="simulateur"
      data-testid="section-simulateur"
      className="relative overflow-hidden bg-forest-deep py-24 sm:py-32"
    >
      <div className="pointer-events-none absolute left-1/2 top-0 h-[500px] w-[900px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[120px]" />
      <div className="relative mx-auto max-w-3xl px-5 sm:px-8">
        <div className="text-center">
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-emerald-300 sm:text-xs">
            Simulateur d'aides & éligibilité 2026
          </p>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-5xl">
            Découvrez vos aides en 2 minutes.
          </h2>
        </div>

        <div className="mt-12 rounded-3xl border border-white/10 bg-forest-surface/80 p-6 backdrop-blur-xl sm:p-10">
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="success"
                data-testid="simulator-success"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="py-8 text-center"
              >
                <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-400/15 text-emerald-300">
                  <PartyPopper className="h-7 w-7" />
                </span>
                <h3 className="mt-6 font-display text-2xl font-bold text-white sm:text-3xl">
                  C'est envoyé, {data.contact_name.split(" ")[0]} !
                </h3>
                <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-white/60">
                  Votre estimation :{" "}
                  <strong className="text-emerald-300">{aidsLabel}</strong>. Un
                  conseiller Ener Solutions vous rappelle sous 24 h ouvrées
                  pour valider votre éligibilité et planifier la visite
                  technique.
                </p>
                <a
                  href="tel:+33535759803"
                  data-testid="simulator-success-phone"
                  className="mt-8 inline-flex items-center gap-2.5 rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-all hover:border-emerald-300 hover:text-emerald-300"
                >
                  <Phone className="h-4 w-4" /> Vous préférez appeler ? 05 35
                  75 98 03
                </a>
              </motion.div>
            ) : (
              <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="flex items-center justify-between gap-4">
                  <p className="text-xs font-semibold text-white/50">
                    Étape {step}/4 — {STEPS_META[step - 1]}
                  </p>
                  <p className="text-xs font-semibold text-emerald-300">
                    {Math.round(((step - (done ? 0 : 1)) / 4) * 100)}%
                  </p>
                </div>
                <div className="mt-3 flex gap-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      data-testid={`simulator-progress-step-${i}`}
                      className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                        i <= step ? "bg-emerald-400" : "bg-white/10"
                      }`}
                    />
                  ))}
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.3 }}
                    className="mt-8 space-y-7"
                  >
                    {step === 1 && (
                      <>
                        <Field label="Votre logement">
                          <div className="grid grid-cols-2 gap-3">
                            {OPTIONS.property_type.map((v) => (
                              <Option key={v} field="property_type" value={v} data={data} setData={setData} />
                            ))}
                          </div>
                        </Field>
                        <Field label="Ancienneté du logement">
                          <div className="grid grid-cols-2 gap-3">
                            {OPTIONS.construction_period.map((v) => (
                              <Option key={v} field="construction_period" value={v} data={data} setData={setData} />
                            ))}
                          </div>
                        </Field>
                        <Field label="Surface habitable">
                          <div className="grid grid-cols-2 gap-3">
                            {OPTIONS.surface_range.map((v) => (
                              <Option key={v} field="surface_range" value={v} data={data} setData={setData} />
                            ))}
                          </div>
                        </Field>
                      </>
                    )}

                    {step === 2 && (
                      <>
                        <Field label="Votre chauffage actuel">
                          <div className="grid grid-cols-2 gap-3">
                            {OPTIONS.heating_current.map((v) => (
                              <Option key={v} field="heating_current" value={v} data={data} setData={setData} />
                            ))}
                          </div>
                        </Field>
                        <Field label="Vos émetteurs de chaleur">
                          <div className="grid gap-3 sm:grid-cols-3">
                            {OPTIONS.emitters.map((v) => (
                              <Option key={v} field="emitters" value={v} data={data} setData={setData} />
                            ))}
                          </div>
                        </Field>
                      </>
                    )}

                    {step === 3 && (
                      <>
                        <Field label="Code postal">
                          <input
                            data-testid="simulator-input-postal"
                            inputMode="numeric"
                            maxLength={5}
                            placeholder="ex. 33240"
                            value={data.postal_code}
                            onChange={(e) =>
                              setData((d) => ({ ...d, postal_code: e.target.value.replace(/\D/g, "") }))
                            }
                            className={inputCls}
                          />
                        </Field>
                        <Field label="Personnes dans le foyer">
                          <div className="relative">
                            <select
                              data-testid="simulator-input-household"
                              value={data.household_size}
                              onChange={(e) =>
                                setData((d) => ({
                                  ...d,
                                  household_size: e.target.value,
                                  income_bracket: classifyIncome(d.rfr, e.target.value),
                                }))
                              }
                              className={`${inputCls} appearance-none pr-10 ${data.household_size ? "" : "text-white/40"}`}
                            >
                              <option value="" disabled className="bg-forest-dark">
                                Sélectionnez…
                              </option>
                              {["1", "2", "3", "4", "5", "6+"].map((v) => (
                                <option key={v} value={v} className="bg-forest-dark text-white">
                                  {v} {v === "1" ? "personne" : "personnes"}
                                </option>
                              ))}
                            </select>
                            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/50" />
                          </div>
                        </Field>
                        <Field label="Revenu fiscal de référence du foyer (RFR)">
                          <div className="relative">
                            <input
                              data-testid="simulator-input-rfr"
                              inputMode="numeric"
                              placeholder="ex. 28 500"
                              value={data.rfr === "" ? "" : formatEuro(data.rfr)}
                              onChange={(e) => {
                                const raw = e.target.value.replace(/\D/g, "").slice(0, 7);
                                setData((d) => ({
                                  ...d,
                                  rfr: raw,
                                  income_bracket: classifyIncome(raw, d.household_size),
                                }));
                              }}
                              className={`${inputCls} pr-10`}
                            />
                            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-white/50">
                              €
                            </span>
                          </div>
                          <p className="text-xs leading-relaxed text-white/45">
                            Indiqué sur votre dernier avis d'imposition, ligne
                            « Revenu fiscal de référence ». Additionnez les RFR
                            de tous les avis du foyer.
                          </p>
                          <AnimatePresence mode="wait">
                            {data.income_bracket ? (
                              <motion.div
                                key={data.income_bracket}
                                data-testid="simulator-income-category"
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.25 }}
                                className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-emerald-400/25 bg-emerald-400/5 px-4 py-3"
                              >
                                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-300">
                                  Votre catégorie
                                </span>
                                <span
                                  data-testid="simulator-income-bracket-value"
                                  className="font-display text-base font-bold text-white"
                                >
                                  Ménage aux revenus {data.income_bracket.toLowerCase()}
                                </span>
                                <span className="w-full text-xs text-white/45">
                                  Barème MaPrimeRénov' 2026 hors Île-de-France —
                                  foyer de {data.household_size} pers. : très
                                  modestes ≤ {formatEuro(thresholdsFor(data.household_size)[0])} €,
                                  modestes ≤ {formatEuro(thresholdsFor(data.household_size)[1])} €,
                                  intermédiaires ≤ {formatEuro(thresholdsFor(data.household_size)[2])} €.
                                </span>
                              </motion.div>
                            ) : (
                              data.rfr !== "" && !data.household_size && (
                                <p
                                  key="need-household"
                                  data-testid="simulator-income-need-household"
                                  className="text-xs text-amber-300/80"
                                >
                                  Sélectionnez le nombre de personnes du foyer pour
                                  connaître votre catégorie.
                                </p>
                              )
                            )}
                          </AnimatePresence>
                        </Field>
                      </>
                    )}

                    {step === 4 && (
                      <>
                        <div
                          data-testid="simulator-result"
                          className="rounded-2xl border border-emerald-400/25 bg-emerald-400/5 p-6"
                        >
                          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-300">
                            Votre estimation
                          </p>
                          {aids?.type === "aids" ? (
                            <>
                              <p className="mt-3 font-display text-4xl font-extrabold text-white sm:text-5xl">
                                ≈ {aids.total.toLocaleString("fr-FR")} €
                              </p>
                              <p className="mt-2 text-sm text-white/60">
                                d'aides cumulées MaPrimeRénov' + prime CEE, à
                                laquelle s'ajoute une TVA à 5,5 % et un éco-PTZ
                                jusqu'à 15 000 € pour financer le reste à
                                charge.
                              </p>
                            </>
                          ) : (
                            <p className="mt-3 text-sm leading-relaxed text-white/70">
                              Votre projet mérite une analyse personnalisée par
                              un conseiller — des aides restent possibles selon
                              votre situation. Laissez vos coordonnées, on vous
                              dit tout.
                            </p>
                          )}
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                          <Field label="Prénom & nom">
                            <input
                              data-testid="simulator-input-name"
                              placeholder="ex. Marie Dupont"
                              value={data.contact_name}
                              onChange={(e) => setData((d) => ({ ...d, contact_name: e.target.value }))}
                              className={inputCls}
                            />
                          </Field>
                          <Field label="Téléphone mobile">
                            <input
                              data-testid="simulator-input-phone"
                              type="tel"
                              placeholder="ex. 06 12 34 56 78"
                              value={data.phone}
                              onChange={(e) => setData((d) => ({ ...d, phone: e.target.value }))}
                              className={inputCls}
                            />
                          </Field>
                        </div>
                        <Field label="Adresse email">
                          <input
                            data-testid="simulator-input-email"
                            type="email"
                            placeholder="ex. marie@email.fr"
                            value={data.email}
                            onChange={(e) => setData((d) => ({ ...d, email: e.target.value }))}
                            className={inputCls}
                          />
                        </Field>
                        <label className="flex cursor-pointer items-start gap-3">
                          <input
                            type="checkbox"
                            data-testid="simulator-consent"
                            checked={data.consent}
                            onChange={(e) => setData((d) => ({ ...d, consent: e.target.checked }))}
                            className="mt-0.5 h-4 w-4 accent-emerald-400"
                          />
                          <span className="text-sm leading-relaxed text-white/60">
                            J'accepte d'être recontacté(e) par Ener Solutions
                            au sujet de mon projet de pompe à chaleur.
                          </span>
                        </label>
                        <p className="flex items-center gap-2 text-xs text-white/40">
                          <ShieldCheck className="h-4 w-4 text-emerald-400/60" />
                          Vos données restent confidentielles et ne sont
                          jamais revendues.
                        </p>
                      </>
                    )}
                  </motion.div>
                </AnimatePresence>

                <div className="mt-10 flex items-center justify-between gap-4">
                  {step > 1 ? (
                    <button
                      data-testid="simulator-back"
                      onClick={() => setStep((s) => s - 1)}
                      className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white/70 transition-all hover:border-white/50 hover:text-white"
                    >
                      <ArrowLeft className="h-4 w-4" /> Retour
                    </button>
                  ) : (
                    <span />
                  )}
                  {step < 4 ? (
                    <button
                      data-testid="simulator-next"
                      disabled={!canNext()}
                      onClick={() => setStep((s) => s + 1)}
                      className={`group flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold transition-all ${
                        canNext()
                          ? "bg-emerald-400 text-forest-deep hover:scale-[1.03] hover:bg-emerald-300"
                          : "cursor-not-allowed bg-white/10 text-white/30"
                      }`}
                    >
                      Continuer
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </button>
                  ) : (
                    <button
                      data-testid="simulator-submit"
                      disabled={!canSubmit() || submitting}
                      onClick={submit}
                      className={`flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold transition-all ${
                        canSubmit() && !submitting
                          ? "bg-emerald-400 text-forest-deep hover:scale-[1.03] hover:bg-emerald-300 hover:shadow-glow"
                          : "cursor-not-allowed bg-white/10 text-white/30"
                      }`}
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Envoi…
                        </>
                      ) : (
                        <>
                          Envoyer ma demande <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
